import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { ifProp, theme } from 'styled-tools';
import JWPlayer from '@jwplayer/jwplayer-react';

const PlayerContainer = styled.div`
  width: 100%;
  margin: 0 auto;

  .jwplayer {
    /* Override the JW Player settings so the control bar can float down rather than disappear when a user is inactive */
    &.jw-state-playing {
      &.jw-flag-user-inactive {
        .jw-controlbar {
          opacity: 1 !important;
          visibility: visible !important;
        }

        .jw-slider-time {
          transform: translateY(50px);
          
          ${ifProp('$live', css`
            ${(props) => props.theme.breakpoints.up('lg')} {
              transform: translateY(65px);
            }
          `)}
        }

        .jw-knob {
          opacity: 0;
          transition: opacity 0.3s ease-out;
        }

        .jw-button-container {
          opacity: 0;
          pointer-events: none; /* Prevents accidental clicks while hidden */
          transform: translateY(100%);
        }
      }
    }

    .jw-button-container {
      transition: transform 0.3s ease-out, opacity 0.3s ease-out;
      transform: translateY(0);
      opacity: 1;
    }

    .jw-controlbar {
      .jw-slider-time {
        padding: 0;
        transition: transform 0.3s ease-out, height 0.3s ease-out;
        transform: translateY(0);
      }

      .jw-timesegment-bar {
        &.jw-timesegment-progress {
          background-color: ${theme('colors.ttRed')};
        }
      }
    }
  }
`;

JWVideoPlayer.propTypes = {
  autoplay: PropTypes.oneOf([false, true, 'muted', 'play', 'any']),
  file: PropTypes.string,
  libraryUrl: PropTypes.string,
  live: PropTypes.bool,
  onComplete: PropTypes.func,
  onDuration: PropTypes.func,
  onError: PropTypes.func,
  onSuccess: PropTypes.func,
  poster: PropTypes.string,
  settingsLoading: PropTypes.bool,
  startTime: PropTypes.number,
  videoId: PropTypes.string,
};

function JWVideoPlayer({
  autoplay,
  file,
  libraryUrl,
  live,
  onComplete,
  onDuration,
  onError,
  onSuccess,
  poster,
  settingsLoading,
  startTime,
  videoId,
}) {
  const playerRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);
  const playlistUrl = !file && !live ? `https://cdn.jwplayer.com/v2/media/${videoId}` : null;
  const hasSeenFirstPlay = useRef(false);
  const pendingStartTime = useRef(startTime);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsMounted(true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // Cleanup effect to remove player on unmount
  useEffect(() => {
    return () => {
      if (playerRef.current?.player) {
        try {
          playerRef.current.player.remove();
        } catch (err) {
          console.error('Error removing JW Player:', err);
        }
      }
    };
  }, []);

  const handleReady = () => {
    const wrapper = playerRef.current;
    const player = wrapper?.player;

    if (player) {
      // If we have a start time and autoplay is off, seek on first play
      if (pendingStartTime.current && pendingStartTime.current > 0 && autoplay === false && !live) {
        player.on('play', function seekOnFirstPlay() {
          if (!hasSeenFirstPlay.current) {
            hasSeenFirstPlay.current = true;
            player.seek(pendingStartTime.current);
            player.off('play', seekOnFirstPlay);
          }
        });
      }

      player.on('meta', () => {
        const duration = player.getDuration();
        if (onDuration && Number.isFinite(duration)) {
          onDuration(duration);
        }
      });

      if (onSuccess) {
        onSuccess({ ref: player });
      }
    }
  };

  const handlePlay = () => {
    // Add any logic needed for video playing
  };

  const handlePause = () => {
    // Add any logic needed for video pausing
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
  };

  const handleError = (event) => {
    console.error('Player error', event);

    if (onError) {
      onError(event);
    }
  };

  // Don't render player until we have the required props
  if (!isMounted || !libraryUrl || settingsLoading) {
    return <PlayerContainer />;
  }

  const playerConfig = {
    aspectratio: '16:9',
    width: '100%',
    autostart: autoplay === true || autoplay === 'muted' || autoplay === 'play' || autoplay === 'any',
    mute: autoplay === 'muted',
    playsinline: true,
  };

  // Only use starttime config if autoplay is enabled (it doesn't work with autoplay: false)
  if (startTime && startTime > 0 && (autoplay === true || autoplay === 'muted' || autoplay === 'play' || autoplay === 'any')) {
    playerConfig.starttime = startTime;
  }

  if (live) {
    playerConfig.liveSyncDuration = 30;
  }

  return (
    <PlayerContainer $live={live}>
      <JWPlayer
        ref={playerRef}
        library={libraryUrl}
        image={poster}
        config={playerConfig}
        file={file}
        onReady={handleReady}
        onComplete={handleComplete}
        onError={handleError}
        {...(playlistUrl && { playlist: playlistUrl })}
      />
    </PlayerContainer>
  );
}

export default JWVideoPlayer;
